import { LocationQuery, RouteParams, Router } from 'vue-router'

import PGFaviconWhite192 from '@/assets/icons/favicon-192.forced.png?file'
import PGFaviconWhite512 from '@/assets/icons/favicon-512.forced.png?file'
import PGFaviconWhiteSVG from '@/assets/icons/favicon.svg?file'
import { ROUTE_NAMES, ROUTE_NAMES_VALUES } from '@/router/constants'
import { getGameByParam } from '@/utils/requests/games'
import { isAPIRedirect } from '@/utils/requests/utils'

// import PlaygamaOG from '@/assets/icons/Playgama-OG.webp?file'

type ImageType = 'image/webp' | 'image/png' | 'image/svg+xml'
type ImagePurpose = 'any' | 'maskable'
type Display = 'fullscreen' | 'standalone'
type FormFactor = 'wide' | 'narrow'

interface ManifestIcon {
    src: string
    sizes: string
    type: ImageType
    purpose: ImagePurpose
}

interface ManifestScreenshot {
    src: string
    sizes: string
    type: ImageType
    form_factor?: FormFactor
    label?: string
}

interface RelatedApplication {
    platform: string
    url: string
    id?: string
}

interface BaseManifest {
    name: string
    short_name: string
    id: string
    start_url: string
    display: Display
    background_color: string
    theme_color: string
    icons: ManifestIcon[]
    screenshots: ManifestScreenshot[]
}

interface GameManifest extends BaseManifest {
    display: Display
    scope: string
    description: string
    categories: string[]
    orientation: 'any'
    related_applications: RelatedApplication[]
    prefer_related_applications: boolean
}

// No need for a lot of icons
// Any maskable is discouraged
// You need at least one any icon
// https://web.dev/learn/pwa/web-app-manifest#for_chromium_browsers
export class ManifestGenerator {
    private static readonly CDN_REGEX = /playgama\.com\/cdn-cgi\/imagedelivery\//
    private static readonly CDN_SIZE_REGEX = /\/(thumb|enlarged)$/
    private static readonly MANIFEST_COLORS = {
        background: '#000',
        theme: '#000',
    }

    constructor(private readonly router: Router) {}

    private static isCDNImage(url: string): boolean {
        return ManifestGenerator.CDN_REGEX.test(url)
    }

    private static removeSizeParam(url: string): string {
        return url.replace(ManifestGenerator.CDN_SIZE_REGEX, '/')
    }

    public static getScaledImage(url: string, width: number, height = width): string {
        if (!ManifestGenerator.isCDNImage(url)) {
            return url
        }

        const baseURL = ManifestGenerator.removeSizeParam(url)
        return `${baseURL}fit=cover,w=${width},h=${height}`
    }

    private getRouterPath(name: ROUTE_NAMES_VALUES, params?: RouteParams, query?: LocationQuery): string {
        const { fullPath } = this.router.resolve({
            name,
            params,
            query,
        })

        return fullPath
    }

    public generateMainManifest(clid?: string): BaseManifest {
        const homePath = this.getRouterPath(ROUTE_NAMES.MAIN)
        const startPath = this.getRouterPath(ROUTE_NAMES.MAIN, undefined, clid ? { clid } : undefined)

        return {
            name: 'Playgama.com',
            short_name: 'Playgama',
            id: homePath,
            start_url: startPath,
            display: 'standalone',
            background_color: ManifestGenerator.MANIFEST_COLORS.background,
            theme_color: ManifestGenerator.MANIFEST_COLORS.theme,
            icons: [
                {
                    src: PGFaviconWhiteSVG,
                    sizes: '512x512',
                    type: 'image/svg+xml',
                    purpose: 'maskable',
                },
                {
                    src: PGFaviconWhite192,
                    sizes: '192x192',
                    type: 'image/png',
                    purpose: 'maskable',
                },
                {
                    src: PGFaviconWhite512,
                    sizes: '512x512',
                    type: 'image/png',
                    purpose: 'any',
                },
            ],
            screenshots: [
                // disable native install prompt
                //     {
                //         src: PlaygamaOG,
                //         sizes: '2400x1260',
                //         form_factor: 'wide',
                //         type: 'image/webp',
                //         label: 'Playgama.com cover',
                //     }
            ],
        }
    }

    public async generateGameManifest(hru: string, clid?: string): Promise<GameManifest> {
        const result = await getGameByParam({
            param: hru,
            locale: 'en-US',
            backendFlags: undefined,
        })
        if (isAPIRedirect(result)) {
            throw new Error('The requested game is a duplicate, the request should be redirected')
        }

        const { game } = result
        const { title, description, icon } = game
        const params = { autoplay: 'true' } as Record<string, string>
        if (clid) {
            params.clid = clid
        }

        const startUrl = this.getRouterPath(ROUTE_NAMES.GAME_PAGE, { game: hru }, params)
        const gamePath = this.getRouterPath(ROUTE_NAMES.GAME_PAGE, { game: hru })

        return {
            name: `${title} - Playgama.com`,
            short_name: title,
            id: gamePath,
            start_url: startUrl,
            scope: gamePath,
            display: 'fullscreen',
            background_color: ManifestGenerator.MANIFEST_COLORS.background,
            theme_color: ManifestGenerator.MANIFEST_COLORS.theme,
            description,
            icons: [
                {
                    src: ManifestGenerator.getScaledImage(icon, 512),
                    sizes: '512x512',
                    type: 'image/webp',
                    purpose: 'any',
                },
                {
                    src: ManifestGenerator.getScaledImage(icon, 384),
                    sizes: '384x384',
                    type: 'image/webp',
                    purpose: 'any',
                },
                {
                    src: ManifestGenerator.getScaledImage(icon, 192),
                    sizes: '192x192',
                    type: 'image/webp',
                    purpose: 'any',
                },
            ],
            screenshots: [
                // disable native install prompt
                //     {
                //         src: ManifestGenerator.getScaledImage(preview, 1280, 720),
                //         sizes: '1280x720',
                //         type: 'image/webp',
                //         form_factor: 'wide',
                //         label: `${title} preview`,
                //     },
                //     {
                //         src: ManifestGenerator.getScaledImage(preview, 720, 1280),
                //         sizes: '720x1280',
                //         type: 'image/webp',
                //         label: `${title} preview`,
                //     },
                //     ...(screenshots?.map(
                //         (screenshot): ManifestScreenshot => ({
                //             src: ManifestGenerator.getScaledImage(screenshot, 1280, 720),
                //             sizes: '1280x720',
                //             type: 'image/webp',
                //             form_factor: 'wide',
                //             label: `${title} screenshot`,
                //         }),
                //     ) || []),
            ],
            categories: ['games', 'entertainment'],
            orientation: 'any',
            related_applications: [],
            prefer_related_applications: false,
        }
    }
}
